'use client';

import { useFetchUser } from '@/auth/hooks';
import { useExplorer } from '@/hooks/useExplorer';
import { useRecaptchaBadge } from '@/hooks/useRecaptchaBadge';
import { explorerAnalytics } from '@/lib/analytics';
import { PendingQuery } from '@/lib/pendingQuery';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import APISelector from '@components/_components/ui/APISelector';
import clsx from 'clsx';
import { useInView } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import AICodeGeneratorContainer from './code-generator/AICodeGeneratorContainer';
import { EndpointExtended, Resource } from '@context/openapi/types';

interface ExplorerHomePageProps {
  resources: Resource[];
}

const ExplorerHomePage: React.FC<ExplorerHomePageProps> = ({ resources }) => {
  const { isLoggedIn, loading: authLoading } = useFetchUser();
  const { userPrompt, setUserPrompt, setSelectedSDK } = useExplorer();
  const { aiResponseOperationIds, messages, currentSessionId } =
    useCodeGenerationStore();
  const aiResponseOperations = useMemo<EndpointExtended[]>(() => {
    if (!aiResponseOperationIds) return [];
    return resources.flatMap((resource) =>
      resource.operations
        .filter((op) => aiResponseOperationIds.includes(op.id))
        .map((op) => ({
          resourceId: resource.id,
          id: op.id,
          name: op.name,
          description: op.description,
          method: op.method,
          url: op.url,
          shortDescription: op.shortDescription,
        }))
    );
  }, [aiResponseOperationIds, resources]);
  const hideApiSelector = Boolean(userPrompt) || messages.length > 0;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isScrolledDown, setIsScrolledDown] = useState(false);

  // Show reCAPTCHA badge when AI chat is active
  useRecaptchaBadge(messages.length > 0 && !!currentSessionId);

  // Track homepage view
  useEffect(() => {
    explorerAnalytics.homepageViewed({
      isLoggedIn: !!isLoggedIn,
      resourcesCount: resources.length,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isInView = useInView(textareaRef, {
    amount: 0.01,
  });

  // Track scroll position to determine if we're scrolled down
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolledDown(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore pending query after OAuth redirect
  useEffect(() => {
    if (authLoading || !isLoggedIn) return;

    const pending = PendingQuery.get();
    if (pending.query) {
      setUserPrompt(pending.query);
      if (pending.sdk) {
        setSelectedSDK(pending.sdk);
      }
      PendingQuery.clear();
      // Focus after React re-renders with the restored value
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [isLoggedIn, authLoading, setUserPrompt, setSelectedSDK]);

  return (
    <div
      id="top"
      className={clsx(
        hideApiSelector ? 'space-y-10' : 'space-y-20',
        'min-h-[calc(100vh-84px)] scroll-smooth'
      )}
    >
      <AICodeGeneratorContainer
        textareaRef={textareaRef}
        aiResponseOperations={aiResponseOperations}
      />

      <APISelector
        enablePromptPlaceholder={
          !isInView && messages.length === 0 && isScrolledDown
        }
        resources={resources}
        textareaRef={textareaRef}
        hideApiSelector={hideApiSelector}
      />
    </div>
  );
};

export default ExplorerHomePage;
